import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import ScrollTopButton from '../components/ScrollTopButton';

/**
 * /privacy — Marakin privacy policy (GDPR Week 6).
 *
 * DRAFT — entity details filled 7 Jul 2026 (Marakin Ltd, 17325460; ICO
 * ZC192739, expires 6 Jul 2027 — renew annually). Remaining before final:
 * the R-5 full legal review. Policy version lives in backend lib/consents.js
 * (POLICY_VERSION) and should be bumped when this text changes materially.
 */

const POLICY_VERSION = '2026-07-23-draft';
// 18 Oct 2026 (WT-29b, Stuart): who-sees-names wording corrected (teammates,
// sponsors' customers). POLICY_VERSION deliberately NOT bumped: no consent
// scope changed meaning, and a bump would re-ask or switch off analytics for
// everyone (lib/consents.js REASK_ON_POLICY_CHANGE / AnalyticsGate).
const LAST_UPDATED = '18 October 2026';

const S = ({ title, children }) => (
  <section style={{ marginBottom: 28 }}>
    <h2 style={{ fontSize: 20, marginBottom: 10 }}>{title}</h2>
    {children}
  </section>
);

const P = ({ children }) => (
  <p style={{ fontSize: 15, lineHeight: 1.65, color: 'var(--text-primary)', marginBottom: 10 }}>{children}</p>
);

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/'));
  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)' }}>
      <ScrollTopButton />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: 'calc(40px + var(--sat)) 20px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <button type="button" onClick={goBack}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-secondary)', fontFamily: 'Nunito, sans-serif', fontSize: 14, fontWeight: 700 }}>
            <ChevronLeft size={16} /> Back to club hub
          </button>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center' }}>
            <img src="/marakin-logo-charcoal.png" alt="Marakin" style={{ height: 22 }} />
          </Link>
        </div>

        <h1 style={{ fontSize: 30, marginBottom: 6 }}>Privacy Policy</h1>
        <p style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 32 }}>
          Version {POLICY_VERSION} · Last updated {LAST_UPDATED} · DRAFT for pilot
        </p>

        <S title="Who we are">
          <P>
            Marakin ("we", "us") provides a digital platform that grassroots sports clubs use to
            manage members, teams, events, communications, kit and sponsorship. This policy
            explains how we handle personal data when you use a club's Marakin hub or the
            Marakin app.
          </P>
          <P>
            Data controller: <strong>Marakin Ltd</strong>, a company registered in England and
            Wales (company number <strong>17325460</strong>), registered office
            <strong> 2 Alison Gardens, Backwell, Bristol, BS48 3LT</strong>. Privacy contact:
            <strong> privacy@marakin.co.uk</strong>. ICO registration:
            <strong> ZC192739</strong>.
          </P>
          <P>
            For most day-to-day club data (for example who is in which team), your club decides
            why and how data is used and Marakin processes it on the club's behalf. For platform
            analytics, marketing and advertising audiences described below, Marakin is the
            controller.
          </P>
        </S>

        <S title="What we collect">
          <P>
            <strong>Account basics</strong> — name, email, phone (optional), date of birth,
            password (stored as a one-way hash), the clubs and teams you belong to, and your role
            at each club. Date of birth is required so we can apply the right protections for
            your age.
          </P>
          <P>
            <strong>Club activity</strong> — things you do that the club needs to run: event
            responses (going / not going, and any note you add), announcements you post, QR offer
            codes you claim and redeem, and kit shop activity.
          </P>
          <P>
            <strong>Children you manage</strong> — parents can add children to their account.
            Children under 13 don't get their own login; their profile (name, date of birth,
            team) is managed entirely by their parent or guardian.
          </P>
          <P>
            <strong>Usage events</strong> — only with your consent (see "Your choices"), we keep a
            log of in-app actions such as viewing an offer or responding to an event. Each entry
            records the consent settings and age band in force at the time. We record your
            country at most — never your raw IP address.
          </P>
          <P>
            <strong>Location</strong> — if you turn on "leave-by" journey times, your device
            location is used to calculate travel time. It stays on your device and is sent only
            to our routing service to compute the journey — we never store your location on our
            servers.
          </P>
          <P>
            <strong>Notifications</strong> — if you enable push notifications, we store a device
            token so your club's messages can reach your phone. Disabling notifications or
            logging out removes it.
          </P>
        </S>

        <S title="Your choices — the four layers">
          <P>
            <strong>1. Necessary (always on)</strong> — the minimum needed to run your account
            and your club's hub: your profile, memberships, event responses, security logs. You
            can't opt out of this while you have an account.
          </P>
          <P>
            <strong>2. Analytics (your choice)</strong> — usage insight that helps us improve
            Marakin: aggregate usage events, plus product analytics and session replay through
            PostHog (see "Product analytics &amp; session replay" below). Never used to target
            you with advertising.
          </P>
          <P>
            <strong>3. Marketing (your choice, 16+)</strong> — using your activity to show you
            more relevant sponsor offers inside Marakin.
          </P>
          <P>
            <strong>4. Advertising audiences (your choice, 18+)</strong> — including your
            activity when we build advertising audiences with platforms such as Meta and Google,
            so clubs' sponsors can reach people like their supporters. Under-18s are never
            included, regardless of any setting.
          </P>
          <P>
            You can change layers 2–4 at any time in <strong>My account → Privacy &amp; data</strong>.
            Each change is recorded, and withdrawing consent stops future use immediately.
          </P>
        </S>

        <S title="Product analytics &amp; session replay">
          <P>
            If — and only if — you switch on the <strong>Analytics</strong> choice, we use{' '}
            <strong>PostHog</strong>, a product-analytics service, to understand how Marakin is
            used: which screens are visited, what's tapped or clicked, basic device information
            (device type, operating system, screen size, country), and{' '}
            <strong>session replays</strong> — a reconstruction of how you moved through the app.
          </P>
          <P>
            <strong>Anything you type is never recorded.</strong> All text inputs are masked on
            your device before a replay is created, so passwords, messages and personal details
            you enter don't leave your phone or browser as part of a recording. Analytics data is
            linked to an internal account number only — not your name or email — and your raw IP
            address is not stored.
          </P>
          <P>
            PostHog processes this data for us as a processor, on servers in{' '}
            <strong>Frankfurt, Germany (EU)</strong>. It is used solely to improve Marakin — it is
            never shared with advertisers or ad networks, and it plays no part in sponsor
            reporting. Session replays are automatically deleted by PostHog after a short
            retention period (currently around one month); analytics events are kept no longer
            than 24 months.
          </P>
          <P>
            This is off unless you opt in, and you can withdraw at any time in{' '}
            <strong>My account → Privacy &amp; data</strong> — capture and replay stop
            immediately.
          </P>
        </S>

        <S title="Children">
          <P>
            Marakin accounts are currently for adults aged 18 and over; children and under-18s are
            added and managed by a parent or guardian from their own account and never have a login.
            We follow the ICO Children's Code, with protections built in for younger age groups
            should the age floor ever be lowered: under-13s never have behavioural tracking beyond
            what's strictly necessary; under-16s can't enter marketing data flows; and under-18s are
            never included in third-party advertising audiences. These limits are enforced in our
            systems, not just in settings screens.
          </P>
        </S>

        <S title="Legal bases">
          <P>
            We rely on <strong>contract</strong> (running your account and your club's hub),
            <strong> consent</strong> (analytics, marketing, advertising audiences — and you can
            withdraw it at any time), and <strong>legitimate interests</strong> (service security,
            preventing abuse, and sponsorship reporting: a sponsor sees the redemptions made at
            their own business).
          </P>
        </S>

        <S title="Who we share data with">
          <P>
            <strong>Your club</strong> — club admins, and team managers and coaches for your teams,
            see your profile, role and event responses so they can run the club. Your teammates and
            their parents see your name on your team's lists. A child's name is only shown to their
            parents, their teammates and teammates' parents, their team's coaches and managers, and
            club admins.
          </P>
          <P>
            <strong>Sponsors</strong> — when you use a sponsor's offer, that sponsor sees your name,
            the offer and what you spent, as any shop would. Under-18s are shown as "Club member".
            Sponsors never see your contact details, your browsing or your history.
          </P>
          <P>
            <strong>Service providers</strong> — hosting (Railway), push delivery (Google
            Firebase / Apple), mapping and routing (OpenStreetMap, Mapbox), weather (Open-Meteo),
            and — only if you've opted in to analytics — product analytics and session replay
            (PostHog, hosted in the EU). Each receives only what's needed to provide its service.
          </P>
          <P>
            <strong>Advertising platforms</strong> — only if you're 18+ and have opted in to
            layer 4, and only in hashed/matched audience form. We never sell raw personal data.
          </P>
        </S>

        <S title="How long we keep it">
          <P>
            Account data lasts while your account is active. Usage events are deleted no later
            than 24 months after collection. Consent and audit records are kept as long as we
            need to demonstrate compliance. If you delete your account, personal data is removed
            or anonymised, except where the club has a legitimate need to keep a record (for
            example, match results) or where law requires retention.
          </P>
        </S>

        <S title="Your rights">
          <P>
            You can ask for a copy of your data, ask us to correct it, delete it, export it, or
            object to or restrict its use (UK GDPR Articles 15–22). The quickest route is
            <strong> My account → Privacy &amp; data</strong>, or email
            <strong> privacy@marakin.co.uk</strong>. You can also request account deletion
            without logging in at{' '}
            <Link to="/delete-account" style={{ textDecoration: 'underline' }}>marakin-app-production.up.railway.app/delete-account</Link>.
            We respond within 30 days, and you can complain to the Information Commissioner's
            Office (ico.org.uk).
          </P>
        </S>

        <S title="Changes to this policy">
          <P>
            We'll notify you in-app of material changes and, where required, re-ask for consent.
            The version number above ties every consent you give to the policy text you saw.
          </P>
        </S>

        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 40 }}>
          Marakin · Powering community sport ·{' '}
          <Link to="/terms" style={{ textDecoration: 'underline' }}>terms of service</Link> ·{' '}
          <Link to="/" style={{ textDecoration: 'underline' }}>marakin home</Link>
        </p>
      </div>
    </div>
  );
}
